// Dados que a Pages Function já buscou para a página (proposta, lista, categorias), embutidos
// num <script type="application/json"> (não executável: a CSP não bloqueia). Entram no
// React Query antes da primeira renderização, e a página nasce pronta, sem "carregando".
// Só chegam na primeira visita e a buscadores: com o service worker, a navegação abre a
// casca do app, sem esses dados, e as telas buscam tudo normalmente.
import { hashKey, hydrate, type DehydratedState, type QueryClient, type QueryKey } from '@tanstack/react-query'
import { shouldPersistQuery } from './query-persist'

/** id do <script> com os dados. Mesmo valor de INITIAL_DATA_ID em functions/_lib/pages.ts. */
export const INITIAL_DATA_ID = 'dados-iniciais'

/** Formato gravado pela Function: horário dos dados e as consultas já respondidas. */
type InitialData = { updatedAt: number; queries: { queryKey: QueryKey; data: unknown }[] }

/**
 * Converte o conteúdo do <script> para o formato do `hydrate`. Entram só as consultas que
 * também podem ir para o cache salvo (públicas, iguais para qualquer visitante). Conteúdo
 * inválido é ignorado.
 */
export function readInitialData(text: string | null | undefined): DehydratedState | undefined {
  if (!text) return undefined
  let parsed: Partial<InitialData> | null
  try {
    parsed = JSON.parse(text) as Partial<InitialData> | null
  } catch {
    return undefined
  }
  const updatedAt = parsed?.updatedAt
  if (typeof updatedAt !== 'number' || !Array.isArray(parsed?.queries)) return undefined

  const queries = parsed.queries.filter(
    ({ queryKey, data }) =>
      Array.isArray(queryKey) && data !== undefined && shouldPersistQuery({ queryKey, state: { status: 'success', data } }),
  )
  if (queries.length === 0) return undefined

  return {
    mutations: [],
    queries: queries.map(({ queryKey, data }) => ({
      queryKey,
      queryHash: hashKey(queryKey),
      dehydratedAt: updatedAt,
      state: {
        data,
        dataUpdatedAt: updatedAt,
        dataUpdateCount: 1,
        error: null,
        errorUpdatedAt: 0,
        errorUpdateCount: 0,
        fetchFailureCount: 0,
        fetchFailureReason: null,
        fetchMeta: null,
        isInvalidated: false,
        status: 'success',
        fetchStatus: 'idle',
      },
    })),
  }
}

/**
 * Coloca os dados embutidos no cache. Como o cache salvo no navegador, passa pelo `hydrate`:
 * vale o dado mais novo, e os embutidos nunca sobrescrevem um mais recente.
 */
export function hydrateInitialData(queryClient: QueryClient): void {
  const element = document.getElementById(INITIAL_DATA_ID)
  const state = readInitialData(element?.textContent)
  if (state) hydrate(queryClient, state)
  element?.remove()
}
